/**
 * agentCalibrationService — AGENT-CALIB-1 substrate (Council 009 / EXEC-43 + EXEC-15).
 *
 * Closed-loop confidence calibration for AEGIS-governed agents. Records
 * (predicted_confidence, actual_correctness) per decision per agent, computes
 * per-agent calibration curves, and applies a learned post-hoc adjustment
 * (Platt scaling) when rolling-window miscalibration exceeds threshold.
 *
 * Design pattern (matches kbrainObservationService + attestationService):
 * - Fire-and-forget from governedAegisCall. Errors caught internally — calibration
 *   loss NEVER blocks the AEGIS call (KTRUST-L1 resilience principle).
 * - All Firestore I/O via DataService (FOUNDATION PURITY).
 * - All event emission via EventBusService (EVENTBUS CONTRACT DOCTRINE).
 *
 * Patent anchor: F-AGENT-CALIB-001.
 * Event contract: docs/sessions/AGENT-CALIB-1/event-contract.md.
 */

import DataService from '../DataService.js';
import EventBusService from '../EventBusService.js';
import {
  buildCurve,
  expectedCalibrationError,
  brierScore,
  fitPlatt,
  applyCalibration,
  isMeaningfulAdjustment,
  shouldRecompute,
  DEFAULT_ECE_THRESHOLD,
} from './calibrationCurve.js';

export const AGENT_CONFIDENCE_EMITTED = 'agent.confidence.emitted';
export const AGENT_CALIBRATION_ADJUSTED = 'AGENT-CALIBRATION-ADJUSTED';

const OUTCOMES_COLLECTION = 'agent_calibration_outcomes';
const CURVES_COLLECTION = 'agent_calibration_curves';
const CONFIG_COLLECTION = 'agent_calibration_config';

const DEFAULT_WINDOW_SIZE = 200;
const CALIBRATION_FN_VERSION = 'platt-v1.0';

/**
 * In-process cache of active calibration functions per (tenantId, agentId, decisionType).
 * Refreshed on each adjustment. Keyed by `${tenantId}::${agentId}::${decisionType}`.
 * Avoids a Firestore round-trip on every governed call.
 */
const _calibrationCache = new Map();

/**
 * Registry of decisionType → async (decisionId, context) → 0|1|null fetchers.
 * Domain teams (TMS dispatch, CX inbound, KPOL, etc.) register their outcome
 * fetchers here. The service polls them on a delay to resolve `actualCorrect`.
 */
const _outcomeFetchers = new Map();

function _cacheKey(tenantId, agentId, decisionType) {
  return `${tenantId}::${agentId}::${decisionType}`;
}

function _now() {
  return new Date().toISOString();
}

/**
 * Record a fresh prediction. Called from governedAegisCall after the result
 * comes back. Returns the calibrated confidence (or raw if no curve active).
 *
 * @param {object} args
 * @param {string} args.tenantId
 * @param {string} args.agentId
 * @param {string} args.decisionId    — matches aegis_decisions/<decisionId>
 * @param {string} args.decisionType
 * @param {string} args.module
 * @param {number} args.predictedConfidence
 * @returns {Promise<{ rawConfidence, calibratedConfidence, calibrationApplied, calibrationFnVersion }>}
 */
export async function recordPrediction({
  tenantId,
  agentId,
  decisionId,
  decisionType,
  module: moduleName,
  predictedConfidence,
}) {
  if (!tenantId || !agentId || !decisionId || !decisionType || predictedConfidence == null) {
    return {
      rawConfidence: predictedConfidence,
      calibratedConfidence: predictedConfidence,
      calibrationApplied: false,
      calibrationFnVersion: null,
    };
  }

  const fn = await _loadCalibrationFn(tenantId, agentId, decisionType);
  const calibratedConfidence = applyCalibration(predictedConfidence, fn);
  const calibrationApplied = fn != null && calibratedConfidence !== predictedConfidence;

  const outcomeDoc = {
    tenantId,
    agentId,
    decisionId,
    decisionType,
    module: moduleName || null,
    predictedConfidence,
    calibratedConfidence,
    calibrationApplied,
    calibrationFnVersion: calibrationApplied ? CALIBRATION_FN_VERSION : null,
    actualCorrect: null,
    outcomeSource: null,
    predictedAt: _now(),
    resolvedAt: null,
  };

  // Fire-and-forget — errors logged but never thrown back to caller.
  DataService.create(OUTCOMES_COLLECTION, outcomeDoc, 'agent-calibration', 'agent-calibration')
    .catch(err => console.warn('[agentCalibration] recordPrediction write failed (non-fatal):', err?.message));

  EventBusService.publish({
    eventType: AGENT_CONFIDENCE_EMITTED,
    tenantId,
    sourceModule: 'agent-calibration',
    sourceId: decisionId,
    payload: {
      agentId,
      decisionId,
      decisionType,
      module: moduleName || null,
      rawConfidence: predictedConfidence,
      calibratedConfidence,
      calibrationApplied,
      calibrationFnVersion: calibrationApplied ? CALIBRATION_FN_VERSION : null,
    },
    actor: { id: agentId || 'agent-calibration', role: 'calibration_substrate' },
  }).catch(err => console.warn('[agentCalibration] event publish failed (non-fatal):', err?.message));

  return {
    rawConfidence: predictedConfidence,
    calibratedConfidence,
    calibrationApplied,
    calibrationFnVersion: calibrationApplied ? CALIBRATION_FN_VERSION : null,
  };
}

/**
 * Record the ground-truth outcome for a previously-predicted decision.
 *
 * @param {object} args
 * @param {string} args.tenantId
 * @param {string} args.decisionId
 * @param {0|1} args.actualCorrect
 * @param {string} args.source — 'operator_override' | 'hitl_approval' | 'downstream_outcome' | string
 * @returns {Promise<{ updated: boolean, recomputeTriggered: boolean }>}
 */
export async function recordOutcome({ tenantId, decisionId, actualCorrect, source }) {
  if (!tenantId || !decisionId || (actualCorrect !== 0 && actualCorrect !== 1)) {
    return { updated: false, recomputeTriggered: false };
  }

  // Locate the prediction row. Outcomes are keyed by decisionId; we query then update.
  let rows;
  try {
    rows = await DataService.queryFlex(OUTCOMES_COLLECTION, [
      { field: 'tenantId', op: '==', value: tenantId },
      { field: 'decisionId', op: '==', value: decisionId },
    ], { limit: 1 });
  } catch (err) {
    console.warn('[agentCalibration] recordOutcome query failed (non-fatal):', err?.message);
    return { updated: false, recomputeTriggered: false };
  }
  if (!rows || rows.length === 0) {
    return { updated: false, recomputeTriggered: false };
  }
  const row = rows[0];
  if (row.actualCorrect != null) {
    // Idempotent: an outcome already exists. First write wins.
    return { updated: false, recomputeTriggered: false };
  }

  try {
    await DataService.update(OUTCOMES_COLLECTION, row.id, {
      actualCorrect,
      outcomeSource: source || 'unknown',
      resolvedAt: _now(),
    }, 'agent-calibration');
  } catch (err) {
    console.warn('[agentCalibration] recordOutcome update failed (non-fatal):', err?.message);
    return { updated: false, recomputeTriggered: false };
  }

  // Opportunistic recompute: roll up the agent+decisionType window.
  const recomputeTriggered = await _maybeRecompute({
    tenantId,
    agentId: row.agentId,
    decisionType: row.decisionType,
  });

  return { updated: true, recomputeTriggered };
}

/**
 * Register an outcome-fetcher for a decisionType. Domain teams call this on
 * module init. Fetcher signature: `async (decisionId, ctx) => 0 | 1 | null`.
 */
export function registerOutcomeFetcher(decisionType, fetcher) {
  if (typeof fetcher !== 'function') return;
  _outcomeFetchers.set(decisionType, fetcher);
}

export function _clearOutcomeFetchers() {
  // Test-only.
  _outcomeFetchers.clear();
}

/**
 * Run all registered outcome fetchers against unresolved predictions older
 * than `agedMs` (default 24h). Designed to be called by a CF scheduler or a
 * manual ops command. Returns the number of outcomes resolved.
 */
export async function resolvePendingOutcomes({ tenantId, agedMs = 24 * 60 * 60 * 1000 } = {}) {
  if (!tenantId) return 0;
  let pending;
  try {
    pending = await DataService.queryFlex(OUTCOMES_COLLECTION, [
      { field: 'tenantId', op: '==', value: tenantId },
      { field: 'actualCorrect', op: '==', value: null },
    ], { limit: 500 });
  } catch (err) {
    console.warn('[agentCalibration] resolvePendingOutcomes query failed:', err?.message);
    return 0;
  }
  const cutoff = Date.now() - agedMs;
  let resolved = 0;
  for (const row of pending || []) {
    const predictedAt = row.predictedAt ? new Date(row.predictedAt).getTime() : 0;
    if (predictedAt >= cutoff) continue;
    const fetcher = _outcomeFetchers.get(row.decisionType);
    if (!fetcher) continue;
    let result;
    try {
      result = await fetcher(row.decisionId, { tenantId, agentId: row.agentId });
    } catch (err) {
      console.warn(`[agentCalibration] outcome fetcher for ${row.decisionType} threw:`, err?.message);
      continue;
    }
    if (result !== 0 && result !== 1) continue;
    const r = await recordOutcome({
      tenantId,
      decisionId: row.decisionId,
      actualCorrect: result,
      source: 'downstream_outcome',
    });
    if (r.updated) resolved += 1;
  }
  return resolved;
}

/**
 * Recompute the calibration curve and fit for one (agentId, decisionType) pair.
 * Public entry; also called internally from recordOutcome.
 */
export async function recomputeCurves({ tenantId, agentId, decisionType, windowSize } = {}) {
  if (!tenantId || !agentId || !decisionType) {
    return { recomputed: false, reason: 'missing_args' };
  }
  const size = windowSize || (await _getWindowSize(tenantId));

  let rows;
  try {
    rows = await DataService.queryFlex(OUTCOMES_COLLECTION, [
      { field: 'tenantId', op: '==', value: tenantId },
      { field: 'agentId', op: '==', value: agentId },
      { field: 'decisionType', op: '==', value: decisionType },
    ], { orderBy: 'predictedAt', direction: 'desc', limit: size });
  } catch (err) {
    console.warn('[agentCalibration] recomputeCurves query failed:', err?.message);
    return { recomputed: false, reason: 'query_failed' };
  }
  const labeled = (rows || []).filter(r => r.actualCorrect === 0 || r.actualCorrect === 1);
  if (labeled.length < 20) {
    return { recomputed: false, reason: 'insufficient_labeled_samples', sampleCount: labeled.length };
  }
  const samples = labeled.map(r => ({ predicted: r.predictedConfidence, actual: r.actualCorrect }));
  const curve = buildCurve(samples);
  const ece = expectedCalibrationError(curve);
  const brier = brierScore(samples);

  const prior = await _loadCurveDoc(tenantId, agentId, decisionType);
  const prevFn = prior?.calibrationFn || null;
  const prevECE = prior?.ece ?? null;
  const prevBrier = prior?.brier ?? null;

  const newFit = fitPlatt(samples);
  const newFn = newFit.converged ? { method: 'platt', params: newFit.params } : prevFn;
  const meaningful = isMeaningfulAdjustment(prevFn, newFn);

  const curveDoc = {
    tenantId,
    agentId,
    decisionType,
    sampleCount: labeled.length,
    curve,
    ece,
    brier,
    calibrationFn: newFn,
    fitConverged: newFit.converged,
    fitIterations: newFit.iterations,
    computedAt: _now(),
  };
  const docId = `${agentId}::${decisionType}`;
  try {
    if (prior) {
      await DataService.update(CURVES_COLLECTION, prior.id || docId, curveDoc, 'agent-calibration');
    } else {
      await DataService.create(CURVES_COLLECTION, { id: docId, ...curveDoc }, 'agent-calibration', 'agent-calibration');
    }
  } catch (err) {
    console.warn('[agentCalibration] recomputeCurves write failed:', err?.message);
    return { recomputed: false, reason: 'write_failed' };
  }

  // Refresh in-process cache
  _calibrationCache.set(_cacheKey(tenantId, agentId, decisionType), newFn);

  if (meaningful) {
    const triggerReason = !prevFn ? 'first_fit' : (ece > DEFAULT_ECE_THRESHOLD ? 'ece_threshold' : 'brier_regression');
    EventBusService.publish({
      eventType: AGENT_CALIBRATION_ADJUSTED,
      tenantId,
      sourceModule: 'agent-calibration',
      sourceId: `${agentId}::${decisionType}`,
      payload: {
        agentId,
        decisionType,
        sampleCount: labeled.length,
        prevECE,
        newECE: ece,
        prevBrier,
        newBrier: brier,
        prevCalibrationFn: prevFn,
        newCalibrationFn: newFn,
        triggerReason,
      },
    }).catch(err => console.warn('[agentCalibration] adjusted event publish failed:', err?.message));
  }

  return {
    recomputed: true,
    sampleCount: labeled.length,
    ece,
    brier,
    meaningfulAdjustment: meaningful,
    calibrationFn: newFn,
  };
}

async function _maybeRecompute({ tenantId, agentId, decisionType }) {
  // Cheap pre-check: only recompute if we'd cross a threshold. We need the
  // current curve's ece + brier to decide.
  const prior = await _loadCurveDoc(tenantId, agentId, decisionType);
  if (!prior) {
    // First fit — try, but recompute may decline if < 20 labeled samples.
    const r = await recomputeCurves({ tenantId, agentId, decisionType });
    return r.recomputed;
  }
  // Recompute the rolling window's current ECE WITHOUT writing.
  let rows;
  try {
    rows = await DataService.queryFlex(OUTCOMES_COLLECTION, [
      { field: 'tenantId', op: '==', value: tenantId },
      { field: 'agentId', op: '==', value: agentId },
      { field: 'decisionType', op: '==', value: decisionType },
    ], { orderBy: 'predictedAt', direction: 'desc', limit: await _getWindowSize(tenantId) });
  } catch {
    return false;
  }
  const labeled = (rows || []).filter(r => r.actualCorrect === 0 || r.actualCorrect === 1);
  if (labeled.length < 20) return false;
  const samples = labeled.map(r => ({ predicted: r.predictedConfidence, actual: r.actualCorrect }));
  const currentCurve = buildCurve(samples);
  const currentECE = expectedCalibrationError(currentCurve);
  const currentBrier = brierScore(samples);
  const decision = shouldRecompute({ ece: currentECE, prevBrier: prior.brier, currentBrier });
  if (!decision.recompute) return false;
  const r = await recomputeCurves({ tenantId, agentId, decisionType });
  return r.recomputed;
}

async function _loadCalibrationFn(tenantId, agentId, decisionType) {
  const key = _cacheKey(tenantId, agentId, decisionType);
  if (_calibrationCache.has(key)) return _calibrationCache.get(key);
  const doc = await _loadCurveDoc(tenantId, agentId, decisionType);
  const fn = doc?.calibrationFn || null;
  _calibrationCache.set(key, fn);
  return fn;
}

async function _loadCurveDoc(tenantId, agentId, decisionType) {
  try {
    const rows = await DataService.queryFlex(CURVES_COLLECTION, [
      { field: 'tenantId', op: '==', value: tenantId },
      { field: 'agentId', op: '==', value: agentId },
      { field: 'decisionType', op: '==', value: decisionType },
    ], { limit: 1 });
    return rows && rows.length > 0 ? rows[0] : null;
  } catch {
    return null;
  }
}

async function _getWindowSize(tenantId) {
  try {
    const rows = await DataService.queryFlex(CONFIG_COLLECTION, [
      { field: 'tenantId', op: '==', value: tenantId },
    ], { limit: 1 });
    const row = rows && rows[0];
    if (row && typeof row.windowSize === 'number' && row.windowSize > 0) return row.windowSize;
  } catch {
    /* swallow */
  }
  return DEFAULT_WINDOW_SIZE;
}

/**
 * Read the latest curve doc for a (agentId, decisionType) pair. Used by the
 * CalibrationCurves screen.
 */
export async function getCalibrationCurve({ tenantId, agentId, decisionType }) {
  return _loadCurveDoc(tenantId, agentId, decisionType);
}

/**
 * List all curve docs for a tenant — used by the CalibrationCurves screen to
 * populate the agent picker.
 */
export async function listCalibrationCurves({ tenantId }) {
  try {
    return await DataService.queryFlex(CURVES_COLLECTION, [
      { field: 'tenantId', op: '==', value: tenantId },
    ], { orderBy: 'computedAt', direction: 'desc' });
  } catch {
    return [];
  }
}

/**
 * Test-only: reset module-level state. Not exported in the default surface.
 */
export function _resetForTest() {
  _calibrationCache.clear();
  _outcomeFetchers.clear();
}

export default {
  recordPrediction,
  recordOutcome,
  registerOutcomeFetcher,
  resolvePendingOutcomes,
  recomputeCurves,
  getCalibrationCurve,
  listCalibrationCurves,
  AGENT_CONFIDENCE_EMITTED,
  AGENT_CALIBRATION_ADJUSTED,
};
